import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Backdrop,
	Box,
	Button,
	CircularProgress,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	Pagination,
	Paper,
	Select,
	SelectChangeEvent,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';

import { pageSizeOptions } from 'constants/constants';

import { extractErrorMessage } from 'features/quotation/helpers';
import { SalesOrderFilters } from './SalesOrderFilters';
import { PageNoResult } from 'features/common/components/PageNoResults';
import { ExportToExcelButton } from 'features/common/components/ExportToExcelButton';
import { ExportExcelOptions, exportToExcel, getSortableFields } from 'features/common/helpers';
import { ISort, ShowAlertState } from 'features/common/types';

import { getChannels, getSalesOrderList, getSalesOrderListToExport } from '../../services';
import { IChannel, ISaleOrderListItem } from '../../types';
import {
	calculateChargedPercent,
	calculateDaysToTravel,
	calculateInvoicedPercent,
	processText,
	transformCustomersCompleteName,
	TransformGrossProfit,
	TransformGrossProfitPercent,
	transformValue,
} from './helper';
import { balanceStyle, ColoredLinearProgress, SubtleLinearProgress, saleOrderStyles } from './styles';
import { headerCells } from './constants';
import SyncProcessNetsuiteOptionsMenu from '../SyncProcessNetsuiteOptionsMenu';
import routes from 'constants/routes';
import { parseSalesOrderListState } from '../../helpers/salesOrderListState';
import { isOperatorOrHigher } from '../../../auth/helpers';
import DealOrCaseOpenDetail from '../../../common/components/DealOrCaseOpenDetail';
import SaleOrderInvoicingStatusCell from '../SaleOrderInvoicingStatusCell';
import { serviceTypeToEmoji } from '../../constants';

const ColoredLinearProgressWithLabel = ({ value }: { value: number }) => (
	<Box position="relative" display="inline-flex" width="100%" alignItems="center">
		<ColoredLinearProgress variant="determinate" value={Math.min(100, value)} sx={{ width: '100%' }} />
		<Box
			position="absolute"
			left="50%"
			top="50%"
			sx={{
				transform: 'translate(-50%, -50%)',
				color: 'text.primary',
			}}
		>
			<Typography variant="subtitle2" component="div" color="textSecondary">
				{`${Math.round(value)}%`}
			</Typography>
		</Box>
	</Box>
);

/** Barra secundaria de bajo contraste: el dato importa, pero no debe competir con "% Cobrado". */
const SubtleLinearProgressWithLabel = ({ value }: { value: number }) => (
	<Box display="inline-flex" width="100%" alignItems="center" gap={0.75}>
		<SubtleLinearProgress variant="determinate" value={Math.min(100, value)} sx={{ flex: 1, minWidth: 40 }} />
		<Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: 'right' }}>
			{`${Math.round(value)}%`}
		</Typography>
	</Box>
);

export const SalesOrderList = (): JSX.Element => {
	const classes = saleOrderStyles();
	const history = useHistory();
	const location = useLocation();
	const initialListState = useMemo(() => parseSalesOrderListState(location.search), [location.search]);
	const [loading, setLoading] = useState<boolean>(false);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [currentPage, setCurrentPage] = useState<number>(initialListState.page);
	const [sortFields, setSortFields] = useState<ISort[]>(initialListState.sortFields);
	const [currentSize, setCurrentSize] = useState<number>(initialListState.size);
	const [pageTotal, setPageTotal] = useState<number>(0);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [filterApplied, setFilterApplied] = useState<Record<string, string | number | boolean>>(
		initialListState.filterApplied,
	);
	const [salesOrderList, setSalesOrderList] = useState<ISaleOrderListItem[]>([]);
	const colSpan = headerCells.length;
	const [searchValue, setSearchValue] = useState<string>(initialListState.search);
	const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>(initialListState.search);
	const [channels, setChannels] = useState<IChannel[]>([]);
	const skipUrlHydrationRef = useRef(true);
	const filterAppliedRef = useRef(filterApplied);

	useEffect(() => {
		filterAppliedRef.current = filterApplied;
	}, [filterApplied]);

	useEffect(() => {
		if (skipUrlHydrationRef.current) {
			skipUrlHydrationRef.current = false;
			return;
		}

		const next = parseSalesOrderListState(location.search);
		setCurrentPage(next.page);
		setSortFields(next.sortFields);
		setCurrentSize(next.size);
		setFilterApplied(next.filterApplied);
		setSearchValue(next.search);
		setDebouncedSearchValue(next.search);
	}, [location.search]);

	// Debounce para búsqueda
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchValue(searchValue);
		}, 500);

		return () => clearTimeout(timer);
	}, [searchValue]);

	// Resetear página cuando cambia búsqueda con debounce (solo si realmente cambió)
	const prevDebouncedSearch = useRef(debouncedSearchValue);
	useEffect(() => {
		if (prevDebouncedSearch.current !== debouncedSearchValue && debouncedSearchValue !== '') {
			setCurrentPage(1);
			prevDebouncedSearch.current = debouncedSearchValue;
		}
	}, [debouncedSearchValue]);

	const handleFiltersChange = useCallback((filters: Record<string, string | number | boolean>) => {
		const serialized = JSON.stringify(filters);
		if (serialized === JSON.stringify(filterAppliedRef.current)) return;

		localStorage.setItem('salesOrderFilters', serialized);
		filterAppliedRef.current = filters;
		setFilterApplied(filters);
		setCurrentPage(1);
		if (Object.keys(filters).length === 0) {
			setSearchValue('');
			setDebouncedSearchValue('');
		}
	}, []);

	const handleClearFilters = useCallback(() => {
		setFilterApplied({});
		setCurrentPage(1);
		setSearchValue('');
		setDebouncedSearchValue('');
	}, []);

	const loadChannels = async () => {
		try {
			const response = await getChannels();
			setChannels(response.data);
		} catch (error: any) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al obtener los canales'),
			});
		}
	};

	useEffect(() => {
		loadChannels();
	}, []);

	const loadSalesOrderList = useCallback(async () => {
		try {
			setLoading(true);
			const results = await getSalesOrderList(
				currentPage,
				currentSize,
				sortFields,
				filterApplied,
				debouncedSearchValue,
			);
			setSalesOrderList(results.data.elements || []);
			setPageTotal(Math.ceil(results.data.total / currentSize));
			setTotalCount(results.data.total);
			setLoading(false);
		} catch (error) {
			console.error('Failed to load data:', error);
			setAlert({ show: true, severity: 'error', message: 'Error al cargar los datos.' });
			setLoading(false);
		}
	}, [currentPage, currentSize, sortFields, filterApplied, debouncedSearchValue]);

	useEffect(() => {
		loadSalesOrderList();
	}, [loadSalesOrderList]);

	const handleVerMasClick = (id: string, matchSearchValueWithServiceId?: boolean) => () => {
		let newUrl = routes.salesOrder.edit.replace(':id', id);
		if (matchSearchValueWithServiceId) {
			newUrl = newUrl + `?serviceId=${debouncedSearchValue}`;
		}
		window.open(newUrl, '_blank');
	};

	const sortableFields = getSortableFields(headerCells);

	const handleExportToExcel = async () => {
		try {
			setLoading(true);
			const title = 'Listado de órdenes de ventas';
			const items = (await getSalesOrderListToExport(sortFields, filterApplied ?? {})).data;
			const optionsToExport = {
				title: title,
				headers: headerCells,
				widthColumns: [
					20, 20, 20, 15, 30, 20, 20, 30, 10, 15, 20, 20, 20, 20, 30, 30, 20, 15, 15, 25, 25, 25, 15, 15, 35, 35,
				],
				filename: title,
			} as ExportExcelOptions;
			exportToExcel(items, optionsToExport, transformValue);
			setLoading(false);
		} catch (error) {
			console.error('Failed to export:', error);
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al exportar el listado'),
			});
			setLoading(false);
		}
	};

	const handleSearch = () => {
		// El debounce ya maneja el cambio, solo necesitamos resetear página si se hace clic explícito
		setDebouncedSearchValue(searchValue);
		setCurrentPage(1);
	};

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value);
	};

	const handleCloseAlert = () => {
		setAlert(defaultAlertState);
	};

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		setCurrentSize(+event.target.value);
		setCurrentPage(1);
	};
	const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
		setCurrentPage(page);
	};

	const handleGoNewSalesOrder = () => {
		history.push('/salesorder/new');
	};

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { auth } = useSelector((state: RootState) => state);

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<>
				<Backdrop className={classes.backdrop} open={loading}>
					<CircularProgress color="inherit" />
				</Backdrop>
				<Grid
					container
					spacing={isMobile ? 2 : 1}
					sx={{
						justifyContent: 'center',
					}}
				>
					<Grid
						container
						spacing={isMobile ? 2 : 2}
						sx={{
							maxWidth: '90vw',
							px: isMobile ? 2 : 0,
						}}
					>
						{/* Botón Nueva Orden */}
						<Grid item xs={12} md={2}>
							<Button
								variant="contained"
								onClick={handleGoNewSalesOrder}
								fullWidth={isMobile}
								sx={{
									height: isMobile ? '48px' : 'auto',
									fontSize: isMobile ? '1rem' : '0.875rem',
									fontWeight: 'bold',
								}}
							>
								+ Nueva Orden
							</Button>
						</Grid>

						{/* Campo de búsqueda - ocupa el ancho completo en mobile */}
						<Grid item xs={12} md={4}>
							<Tooltip title="Buscá por Deal, Cliente o Pasajero Principal, id de sale order o id de servicio">
								<TextField
									label="Buscar"
									variant="outlined"
									size={isMobile ? 'medium' : 'small'}
									fullWidth
									value={searchValue}
									onChange={handleChange}
									sx={{
										'& .MuiOutlinedInput-root': {
											height: isMobile ? '48px' : 'auto',
										},
									}}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton onClick={handleSearch}>
													<SearchIcon />
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
							</Tooltip>
						</Grid>

						{/* Filtros */}
						<Grid item xs={12}>
							<SalesOrderFilters
								key={location.search || 'default'}
								onFiltersChange={handleFiltersChange}
								onClearFilters={handleClearFilters}
								channels={channels.map((ch) => ({ id: ch.id, name: ch.name }))}
								initialFilters={initialListState.initialFilters}
								initialEndpointFilters={initialListState.filterApplied}
							/>
						</Grid>

						{/* Botones de acción - alineados en mobile */}
						<Grid item xs={12} md={2}>
							{isOperatorOrHigher(auth.user) && (
								<Box
									sx={{
										display: 'flex',
										flexDirection: isMobile ? 'row' : 'row',
										gap: isMobile ? 1 : 0,
										paddingLeft: isMobile ? 0 : 2,
										justifyContent: isMobile ? 'center' : 'flex-start',
										alignItems: 'center',
									}}
								>
									<ExportToExcelButton
										disabled={salesOrderList.length == 0}
										handleExportToExcel={handleExportToExcel}
									/>
									<SyncProcessNetsuiteOptionsMenu setLoading={setLoading} onSetAlert={setAlert} />
								</Box>
							)}
						</Grid>
					</Grid>
					<Grid
						item
						xs={12}
						sx={{
							m: isMobile ? 2 : 5,
							ml: isMobile ? 3 : 19,
							justifyContent: 'flex-start',
						}}
					>
						<Typography
							variant={isMobile ? 'body1' : 'subtitle1'}
							sx={{
								fontWeight: isMobile ? 'bold' : 'normal',
								textAlign: 'left',
								fontSize: isMobile ? '1rem' : '1.25rem',
							}}
						>
							En pantalla: {salesOrderList.length} resultados de {totalCount}
						</Typography>
					</Grid>
					<TableContainer
						component={Paper}
						sx={{
							maxWidth: '90vw',
							overflowX: 'auto',
							mx: isMobile ? 1 : 0,
							width: isMobile ? 'calc(100vw - 32px)' : 'auto',
						}}
						className={classes.tableContainer}
					>
						<Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
							<TableHead>
								<TableRow>
									{headerCells.map((cell, index) => {
										const isSticky = !isSmallScreen
											? index === 0
												? classes.stickyFirst
												: index === 1
												? classes.stickySecond
												: index === 2
												? classes.stickyThird
												: ''
											: '';
										const sortDirection = sortFields.find((x) => x.field === cell.field)
											? sortFields.find((x) => x.field === cell.field)?.order
											: false;

										return (
											<TableCell
												className={`${classes.cellNoWrap} ${isSticky}`}
												key={cell.field}
												align="center"
												sortDirection={sortDirection}
											>
												{cell.sortable ? (
													<TableSortLabel
														active={sortFields.some((x) => x.field === cell.field)}
														direction={sortDirection || 'asc'}
														onClick={() => {
															const field = sortFields.find((x) => x.field === cell.field);
															let newSortsValues: ISort[];

															if (field === undefined) {
																// Sin orden → asc
																newSortsValues = [...sortFields, { field: cell.field, order: 'asc' }];
															} else if (field.order === 'asc') {
																// asc → desc
																newSortsValues = sortFields.map((x) =>
																	x.field === cell.field ? { ...x, order: 'desc' as const } : x,
																);
															} else {
																// desc → asc (ciclo)
																newSortsValues = sortFields.map((x) =>
																	x.field === cell.field ? { ...x, order: 'asc' as const } : x,
																);
															}

															newSortsValues.sort(
																(a, b) => sortableFields.indexOf(a.field) - sortableFields.indexOf(b.field),
															);
															setSortFields(newSortsValues);
															setCurrentPage(1);
														}}
													>
														{cell.headerName}
													</TableSortLabel>
												) : (
													cell.headerName
												)}
											</TableCell>
										);
									})}
								</TableRow>
							</TableHead>
							<TableBody>
								{salesOrderList.length === 0 ? (
									<TableRow>
										<TableCell colSpan={colSpan}>
											<PageNoResult />
										</TableCell>
									</TableRow>
								) : (
									salesOrderList.map((salesOrderItem) => (
										<React.Fragment key={salesOrderItem.id}>
											<TableRow
												key={salesOrderItem.id}
												sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
												onDoubleClick={handleVerMasClick(
													salesOrderItem.id,
													salesOrderItem.matchSearchValueWithServiceId,
												)}
												className={classes.hoverRow}
											>
												<TableCell
													align="center"
													className={`${classes.cellNoWrap} ${!isSmallScreen ? classes.stickyFirst : ''} ${
														classes.clickableCell
													}`}
													onClick={handleVerMasClick(salesOrderItem.id, salesOrderItem.matchSearchValueWithServiceId)}
												>
													<Tooltip title="Hacé clic para ver detalles en una nueva pestaña">
														<div>{transformValue('created', salesOrderItem.created, salesOrderItem)} </div>
													</Tooltip>
												</TableCell>
												<TableCell
													align="center"
													className={`${classes.cellNoWrap} ${!isSmallScreen ? classes.stickySecond : ''} ${
														classes.clickableCell
													}`}
													onClick={handleVerMasClick(salesOrderItem.id, salesOrderItem.matchSearchValueWithServiceId)}
												>
													<Tooltip title="Hacé clic para ver detalles en una nueva pestaña">
														<div>{salesOrderItem.createdBy}</div>
													</Tooltip>
												</TableCell>
												<TableCell align="center" className={!isSmallScreen ? classes.stickyThird : ''}>
													<DealOrCaseOpenDetail
														dealId={salesOrderItem.dealId}
														isBitrixDeal={salesOrderItem.isBitrixDeal}
													/>
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{transformCustomersCompleteName(salesOrderItem.customersCompleteName)}
												</TableCell>

												<TableCell align="center">
													<ColoredLinearProgressWithLabel value={calculateChargedPercent(salesOrderItem)} />
												</TableCell>
												<TableCell align="center">
													<SubtleLinearProgressWithLabel value={calculateInvoicedPercent(salesOrderItem)} />
												</TableCell>
												<TableCell align="center">
													<SaleOrderInvoicingStatusCell row={salesOrderItem} />
												</TableCell>
												<TableCell align="center">{salesOrderItem.currency}</TableCell>
												<TableCell align="center">
													{transformValue('salesAmount', salesOrderItem.saleAmount, salesOrderItem)}
												</TableCell>
												<TableCell align="center">
													{transformValue('totalCost', salesOrderItem.totalCost, salesOrderItem)}
												</TableCell>
												<TableCell align="center">
													<TransformGrossProfit grossProfit={salesOrderItem.grossProfit} />
												</TableCell>
												<TableCell align="center">
													<TransformGrossProfitPercent
														grossProfit={salesOrderItem.grossProfit}
														saleAmount={salesOrderItem.saleAmount}
													/>
												</TableCell>
												<TableCell align="center">
													{transformValue('chargedAmount', salesOrderItem.chargedAmount, salesOrderItem)}
												</TableCell>
												<TableCell
													align="center"
													style={balanceStyle(salesOrderItem.pendingPayAmount, salesOrderItem.travelDepartureDate)}
												>
													{transformValue('pendingPayAmount', salesOrderItem.pendingPayAmount, salesOrderItem)}{' '}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{processText(salesOrderItem.mainPassengerCompleteName, 30) || '🧐 Pax principal NO definido'}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{processText(salesOrderItem.destinations, 30)}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{transformValue('travelDepartureDate', salesOrderItem.travelDepartureDate, salesOrderItem)}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{calculateDaysToTravel(salesOrderItem.travelDepartureDate)}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{transformValue('travelReturnDate', salesOrderItem.travelReturnDate, salesOrderItem)}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{salesOrderItem.isReturnDateReached ? '🏁 Sí' : 'No'}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{transformValue('lastSuccessSyncAt', salesOrderItem.lastSuccessSyncAt, salesOrderItem)}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{transformValue('updated', salesOrderItem.updated, salesOrderItem)}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{transformValue('segmentType', salesOrderItem.segmentType, salesOrderItem)}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{transformValue('channel', salesOrderItem.channel?.name, salesOrderItem)}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{transformValue('clientReference', salesOrderItem.clientReference, salesOrderItem)}
												</TableCell>
												<TableCell align="center" className={classes.cellNoWrap}>
													{salesOrderItem.servicesTypes?.map((type) => {
														const service = serviceTypeToEmoji[type];
														return (
															<Tooltip title={service ? service.name : type} key={type}>
																<span style={{ marginRight: 4, cursor: 'pointer' }}>
																	{service ? service.emoji : type}
																</span>
															</Tooltip>
														);
													})}
												</TableCell>
											</TableRow>
										</React.Fragment>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<Grid item xs={12}>
						<Grid container justifyContent="center" flexDirection="row" padding={2}>
							<Grid item xs={12} md={1} textAlign="center">
								<Select value={currentSize} onChange={handleRowsPerPageChange}>
									{pageSizeOptions.map((value) => (
										<MenuItem key={value} value={value}>
											{value}
										</MenuItem>
									))}
								</Select>
							</Grid>
							<Grid item xs={12} md={6} marginTop={1}>
								<Pagination count={pageTotal} page={currentPage} onChange={handlePageChange} color="primary" />
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				<Snackbar open={alert.show} autoHideDuration={2000} onClose={handleCloseAlert}>
					<Alert variant="filled" severity={alert.severity}>
						{alert.message}
					</Alert>
				</Snackbar>
			</>
		</LocalizationProvider>
	);
};
